import type { DocSection } from "./legalDocUtils";
import { longDateText, offerDateProblems, salaryProblem } from "./legalValidation";
import { parseNumber } from "@/tools/converters/numberInput";

/**
 * The offer letter (internship or permanent position) as data: title, sections and the field checks. The website tool
 * and POST /v1/business/offer-letter-draft both use this file, so they write the same letter.
 */
export type PositionType = "internship" | "permanent";
export type PayPeriod = "year" | "month" | "hour";

export interface OfferLetterInput {
  positionType: PositionType;
  companyName: string;
  companyTagline?: string;
  candidateName: string;
  jobTitle: string;
  /** "Full-time", "Part-time", "Contract" ... Default Full-time. */
  employmentType?: string;
  managerName?: string;
  managerTitle?: string;
  mentorName?: string;
  mentorNote?: string;
  startDate: string;
  durationNumber?: string;
  durationUnit?: "weeks" | "months";
  isPaidInternship?: boolean;
  /** The amount as typed ("145000", "145,000"); the currency sign is added when the letter is written. */
  salary: string;
  salaryPeriod?: PayPeriod;
  currencySymbol?: string;
  benefits?: string;
  learningPoints?: string;
  equipmentType?: "own" | "company";
  contingencyCriminalCheck?: boolean;
  contingencyBackgroundCheck?: boolean;
  contingencyReferenceCheck?: boolean;
  contingencyWorkEligibility?: boolean;
  termination?: "at-will" | "notice";
  noticeDays?: string;
  state?: string;
  responseDeadline?: string;
  signerName?: string;
  signerTitle?: string;
  /** true (the website): empty fields show as [Placeholders]. false (the API): the sentence or section is left out instead. */
  placeholders?: boolean;
}

export const DEFAULT_BENEFITS = "Health insurance, paid time off, and participation in company benefit plans as described in the employee handbook.";
export const DEFAULT_MENTOR_NOTE = "who will provide day-to-day guidance on role expectations and task delivery";
export const DEFAULT_LEARNING_POINTS =
  "Engage in high-impact technical and business activities that contribute to real-world product development\nReceive guidance from experienced professionals to develop both technical skills and business acumen\nEnjoy autonomy to explore tools, technologies, and approaches that best support the delivery of assigned work\nReceive a Certificate of Experience upon successful completion of the internship";

/** "145000" -> "145,000"; left as typed when empty or not a number (so a placeholder never becomes "[Amount],00"). */
export function formatMoney(value: string): string {
  const num = Number(value.replace(/,/g, ""));
  return value && Number.isFinite(num) ? num.toLocaleString("en-US") : value;
}

/**
 * Salary text as an API caller might send it: "92000", "$92,000", "92,000 per year", "6,000/month", "45 an hour".
 * Returns the number as digits, the period the words name (if any) and the currency sign (if any).
 */
export function parseSalaryText(raw: string): { amount: string; period?: PayPeriod; currencySymbol?: string } | null {
  const t = raw.trim();
  const m = t.match(/^(.*?)(?:\s*(?:per|\/|a|an|each|every)\s*(year|yr|annum|month|mo|hour|hr)\b\.?|\s+(annually|yearly|monthly|hourly))?\s*$/i);
  if (!m) return null;
  const core = m[1].trim();
  const word = (m[2] ?? m[3] ?? "").toLowerCase();
  const period: PayPeriod | undefined = !word ? undefined : /^(year|yr|annum|annually|yearly)$/.test(word) ? "year" : /^(month|mo|monthly)$/.test(word) ? "month" : "hour";
  const n = parseNumber(core);
  if (n === null) return null;
  const sym = core.match(/[$€£]/)?.[0];
  return { amount: String(n), period, currencySymbol: sym };
}

export interface OfferProblems { salary?: string; startDate?: string; responseDeadline?: string; duration?: string; notice?: string }

export function offerLetterProblems(i: OfferLetterInput, now?: string): OfferProblems {
  const out: OfferProblems = {};
  const internship = i.positionType === "internship";
  if (!internship || i.isPaidInternship) {
    const label = internship ? "Stipend" : "Salary";
    const p = salaryProblem(i.salary, label);
    if (p) out.salary = p;
    else if (!i.salary.trim()) out.salary = `${label} is required.`;
  }
  const d = offerDateProblems(i.startDate, i.responseDeadline ?? "", now);
  if (!i.startDate.trim()) out.startDate = "Enter the start date as YYYY-MM-DD.";
  else if (d.start) out.startDate = d.start;
  if (d.deadline) out.responseDeadline = d.deadline;
  if (internship && (!(Number(i.durationNumber ?? "3") >= 1) || !Number.isInteger(Number(i.durationNumber ?? "3")))) out.duration = "Enter a whole number of 1 or more.";
  if (i.termination === "notice" && (!(Number(i.noticeDays ?? "30") >= 1) || !Number.isInteger(Number(i.noticeDays ?? "30")))) out.notice = "Enter a whole number of 1 or more.";
  return out;
}

export function offerTitle(i: Pick<OfferLetterInput, "positionType">): string {
  return i.positionType === "internship" ? "INTERNSHIP OFFER LETTER" : "OFFER OF EMPLOYMENT";
}

/** The sections in order. The one holding "Sincerely," is where a signature image belongs (the site attaches it there). */
export function buildOfferSections(i: OfferLetterInput, hasSignatureImage = false): DocSection[] {
  const ph = i.placeholders !== false;
  const isInternship = i.positionType === "internship";
  const paid = !!i.isPaidInternship;
  const rel = isInternship ? "internship" : "employment";
  const docTypeLabel = isInternship ? "Internship Offer Letter" : "Offer of Employment";
  const company = i.companyName || (ph ? "[Company Name]" : "the Company");
  const companyThe = i.companyName || "the Company";
  const candidate = i.candidateName || (ph ? "[Candidate Name]" : "the candidate");
  const job = i.jobTitle || (ph ? "[Job Title]" : "the role");
  const cur = i.currencySymbol || "$";
  const period: PayPeriod = i.salaryPeriod ?? "year";
  const amount = `${cur}${formatMoney(i.salary) || (ph ? "[Amount]" : "")}`;
  const employmentType = (i.employmentType || "Full-time").toLowerCase();
  const startLong = i.startDate ? longDateText(i.startDate) : ph ? "[Start Date]" : "";
  const duration = i.durationNumber ?? "3";
  const unit = i.durationUnit ?? "months";

  const contingencies: string[] = [];
  if (i.contingencyCriminalCheck ?? true) contingencies.push(`your self-certification that you have no criminal record that would preclude participation in this ${rel}`);
  if (i.contingencyBackgroundCheck) contingencies.push("successful completion of a background check");
  if (i.contingencyReferenceCheck) contingencies.push("satisfactory completion of reference checks");
  if (i.contingencyWorkEligibility ?? true) contingencies.push("verification of your eligibility to work, including completion of any required work-authorization documentation");

  // Numbered sections are collected first and numbered at the end, so leaving one out never skips a number.
  const numbered: { heading: string; paragraphs: string[]; bullets?: string[] }[] = [];
  numbered.push({
    heading: "Position and Reporting Structure",
    paragraphs: [
      `You will serve as ${job} on a ${employmentType} basis.` +
        (i.managerName ? ` You will report directly to ${i.managerName}${i.managerTitle ? `, ${i.managerTitle}` : ""}.` : "") +
        (i.mentorName ? ` You will also work closely with ${i.mentorName}, ${i.mentorNote ?? DEFAULT_MENTOR_NOTE}.` : ""),
    ],
  });
  numbered.push(isInternship
    ? { heading: "Start Date and Duration", paragraphs: [`Your anticipated start date is ${startLong}. The internship is expected to continue for a period of ${duration || "[Number]"} (${duration || "X"}) ${unit}, subject to satisfactory completion of any pre-internship requirements and ongoing performance.`] }
    : { heading: "Start Date", paragraphs: [`Your anticipated start date is ${startLong}, subject to satisfactory completion of any pre-employment requirements.`] });
  if (isInternship) {
    numbered.push({
      heading: "Compensation",
      paragraphs: paid
        ? [`This internship includes a stipend of ${amount} per ${period}, paid in accordance with ${companyThe}'s standard payroll schedule.`]
        : ["This is an unpaid internship (volunteer position). No salary, stipend, or monetary compensation will be provided. The primary value of this opportunity lies in professional development, hands-on experience, mentorship, and the certificate of completion awarded upon successful conclusion of the internship."],
    });
    numbered.push({ heading: "Learning Opportunities and Mentorship", paragraphs: ["During the internship, you will:"], bullets: (i.learningPoints ?? DEFAULT_LEARNING_POINTS).split("\n").map((l) => l.trim()).filter(Boolean) });
  } else {
    numbered.push({ heading: "Compensation", paragraphs: [`Your starting compensation will be ${amount} per ${period}, paid in accordance with ${companyThe}'s standard payroll schedule.`] });
    numbered.push({ heading: "Benefits", paragraphs: [i.benefits ?? DEFAULT_BENEFITS] });
  }
  numbered.push({
    heading: "Equipment and Confidentiality",
    paragraphs: [
      (i.equipmentType === "own" ? `You will use your personal computing device for ${rel}-related work.` : `${companyThe} will provide the equipment needed for your role.`) +
        ` All work product, code, data, documentation, and other materials created or accessed during your ${rel} are the exclusive property of ${company}. You agree not to disclose, share, or transfer any confidential or proprietary information to any third party without prior written permission. Upon conclusion of your ${rel} (or earlier upon request), you will promptly return or securely transfer all related data and materials.`,
    ],
  });
  const useNotice = i.termination === "notice";
  const noticeDays = i.noticeDays || "30";
  numbered.push({
    heading: useNotice ? "Termination and Notice" : `At-Will Nature of ${isInternship ? "Internship" : "Employment"}`,
    paragraphs: [
      useNotice
        ? `Either you or ${companyThe} may end this ${rel} by giving ${noticeDays} days' written notice, or any longer notice period required by applicable law. ${companyThe === "the Company" ? "The Company" : companyThe} may end the ${rel} immediately, without notice, for serious misconduct to the extent the law allows. Nothing in this letter limits any rights you have under the laws that apply where you work.`
        : `This ${rel} is at-will. Either you or ${companyThe} may terminate the ${rel} relationship at any time, with or without cause or advance notice, subject to applicable law.`,
    ],
  });
  if (contingencies.length > 0) numbered.push({ heading: "Contingencies", paragraphs: [`This offer is contingent upon ${contingencies.join("; ")}.`] });
  if (i.state || ph) numbered.push({ heading: "Governing Law", paragraphs: [`This offer letter and the ${rel} relationship shall be governed by the laws of ${i.state || "[State]"}, without regard to its conflict of laws principles.`] });

  const sections: DocSection[] = [
    {
      paragraphs: [
        `Dear ${candidate},`,
        `We are pleased to offer you the position of ${job} at ${company}${i.companyTagline ? `, ${i.companyTagline}` : ""}. This letter sets forth the terms and conditions of your ${rel}.`,
      ],
    },
    ...numbered.map((s, n) => ({ ...s, heading: `${n + 1}. ${s.heading}` })),
  ];
  const deadlineLong = longDateText(i.responseDeadline ?? "");
  sections.push({
    paragraphs: [
      `We are excited about the possibility of you joining the ${company} team.` +
        (i.responseDeadline ? ` Please indicate your acceptance of this offer by signing and dating below and returning a signed copy of this letter by ${deadlineLong}.` : " Please indicate your acceptance of this offer by signing and dating below and returning a signed copy of this letter.") +
        " We look forward to working with you.",
      "Sincerely,",
    ],
  });
  const signer = i.signerName || (ph ? "[Signer Name]" : "");
  sections.push({
    paragraphs: [
      ...(hasSignatureImage ? [] : ["_________________________________"]),
      ...(signer ? [signer] : []),
      ...(i.signerTitle ? [i.signerTitle] : []),
      company,
      "Date: ____________________",
    ],
  });
  sections.push({
    heading: "ACCEPTANCE OF OFFER",
    paragraphs: [
      `I, ${candidate}, have read and understand the terms of this ${docTypeLabel}. I accept the offer of ${isInternship ? (paid ? "a paid internship" : "an unpaid internship") : "employment"} as ${job} under the terms stated above.`,
      "",
      "_________________________________",
      `${candidate} (Candidate Signature)`,
      "Date: ____________________",
    ],
  });
  return sections;
}
